import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { MAP__fields_by_role } from '@shared/types/types';
import { ENUM__field_graph_interaction } from '../../../_fields/_base/_enums/ENUM__field_graph_interaction';
import { FIELD__list_token } from '../../../_fields/_primitives/list/FIELD__list_token';
import { FIELD_SD__conditioning } from '../../../_fields/guidance/FIELD_SD__conditioning';
import { FIELD__ml_asset_clip } from '../../../_fields/ml_asset/FIELD__ml_asset_clip';
import { FIELD_GRID2D_MODE__all_tokens_except_one } from '../../../_fields/token/FIELD_GRID2D_MODE__all_tokens_except_one';
import { FIELD_GRID2D_MODE__single_token_generation } from '../../../_fields/token/FIELD_GRID2D_MODE__single_token_generation';
import { FIELD_GRID2D_MODE__token_after_token } from '../../../_fields/token/FIELD_GRID2D_MODE__token_after_token';
import { FIELD_GRID2D_MODE__token_vs_token } from '../../../_fields/token/FIELD_GRID2D_MODE__token_vs_token';
import { CONTEXT__graph_prepare_base } from '../../../_graph/CONTEXT__graph_prepare_base';
import { DTO_GRAPH__edge_autoconnect_info_list } from '../../../_graph/DTO_GRAPH__edge_autoconnect_info_list';
import { DTO_GRAPH__instance } from '../../../_graph/DTO_GRAPH__instance';
import { ENUM__invocation_type } from '../../_base/ENUM__invocation_type';
import { INVOCATION_SD__base } from '../INVOCATION_SD__base';
import { INVOCATION_SD__denoise_latents_pipeline } from '../denoise/INVOCATION_SD__denoise_latents_pipeline';
import { INVOCATION_SD__lora_select } from './INVOCATION_SD__lora_select';

export class INVOCATION_SD__text_encoder extends INVOCATION_SD__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static: string = ENUM__dto_class_name.INVOCATION_SD__TEXT_ENCODER;

  /*
   * FIELDS IN
   */

  @FIELD__list_token.init_as_input(
    {
      item_meta: MAP__serializable.from_object({
        ui__has_component: false,
        // graph__interaction: ENUM__field_graph_interaction.HIDDEN,
      }),
    },
    { data__is_required: false },
  )
  tokens!: FIELD__list_token;

  @FIELD__ml_asset_clip.init_as_input({}, { graph__interaction: ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE })
  clip!: FIELD__ml_asset_clip;

  @FIELD_GRID2D_MODE__token_vs_token.init_as_input(
    {},
    {
      graph__interaction: ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE,
      grid2d__can_be_dimension: true,
      ui__has_component: true,
      field_role: 'Token vs. Token',
    },
  )
  grid2d_mode__token_vs_token!: FIELD_GRID2D_MODE__token_vs_token;

  @FIELD_GRID2D_MODE__token_after_token.init_as_input(
    {},
    {
      graph__interaction: ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE,
      grid2d__can_be_dimension: true,
      ui__has_component: true,
      field_role: 'Token after Token',
    },
  )
  grid2d_mode__token_after_token!: FIELD_GRID2D_MODE__token_after_token;

  @FIELD_GRID2D_MODE__single_token_generation.init_as_input(
    {},
    {
      graph__interaction: ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE,
      grid2d__can_be_dimension: true,
      ui__has_component: true,
      field_role: 'Single Token Generation',
    },
  )
  grid2d_mode__single_token_generation!: FIELD_GRID2D_MODE__single_token_generation;

  @FIELD_GRID2D_MODE__all_tokens_except_one.init_as_input(
    {},
    {
      graph__interaction: ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE,
      grid2d__can_be_dimension: true,
      ui__has_component: true,
      field_role: 'All Tokens Except One',
    },
  )
  grid2d_mode__all_tokens_except_one!: FIELD_GRID2D_MODE__all_tokens_except_one;

  /*
   * FIELDS OUT
   */

  @FIELD_SD__conditioning.init_as_output()
  out__positive!: FIELD_SD__conditioning;

  @FIELD_SD__conditioning.init_as_output()
  out__negative!: FIELD_SD__conditioning;

  /*
   * EVENTS
   */
  edges__try_autocreate(graph: DTO_GRAPH__instance) {
    super.edges__try_autocreate(graph);

    /*
     * INVOCATION_SD__text_encoder ->INVOCATION_SD__denoise_latents_pipeline
     */

    let fields_to_denoise = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__denoise_latents_pipeline>();
    fields_to_denoise.invocation_type = INVOCATION_SD__denoise_latents_pipeline;
    fields_to_denoise.add(this.out__positive, ['conditioning_global__positive']);
    fields_to_denoise.add(this.out__negative, ['conditioning_global__negative']);

    this.edges__try_autocreate_from_left_to_right(graph, fields_to_denoise);

    /*
     * INVOCATION_SD__lora_select -> INVOCATION_SD__text_encoder
     */
    let fields_from_image_from_lora_select = new DTO_GRAPH__edge_autoconnect_info_list<INVOCATION_SD__lora_select>();
    fields_from_image_from_lora_select.invocation_type = INVOCATION_SD__lora_select;
    fields_from_image_from_lora_select.add(this.clip.loras, ['loras']);

    this.edges__try_autocreate_from_right_to_left(graph, fields_from_image_from_lora_select);
  }

  /*
   * METHODS
   */

  constructor() {
    super(ENUM__invocation_type.SD__TEXT_ENCODER);
  }

  post_init() {
    super.post_init();
  }

  prepare_for_usage_in(graph: DTO_GRAPH__instance) {
    super.prepare_for_usage_in(graph);

    /*
     *
     */

    let tokens = this.tokens.get_enabled_and_non_zero_weight_items();

    this.grid2d_mode__token_after_token.init_tokens(tokens);
    this.grid2d_mode__token_vs_token.init_tokens(tokens);
    this.grid2d_mode__single_token_generation.init_tokens(tokens);
    this.grid2d_mode__all_tokens_except_one.init_tokens(tokens);
  }

  try_sort_tokens(context: CONTEXT__graph_prepare_base) {
  }

  update_fields_aliases(fields_by_role: MAP__fields_by_role) {
    super.update_fields_aliases(fields_by_role);

    for (let token of this.tokens.items) {
      token.try_set_field_role(fields_by_role, token.weight, `🔤 ${token.word_english.get_value()}`);
    }
  }
}
